import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { BookOpen, Clock, Layers, Plus, Save, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { cleanText, isCleanText } from '@/utils/sanitize';
import { InputField } from '@/components/common/form-fields';
import { Textarea } from '@/components/ui/Textarea';
import { Module } from '@/types/course.types';

const BLOOM_LEVELS: { value: string; label: string }[] = [
  { value: 'remember', label: 'Remember' },
  { value: 'understand', label: 'Understand' },
  { value: 'apply', label: 'Apply' },
  { value: 'analyze', label: 'Analyze' },
  { value: 'evaluate', label: 'Evaluate' },
  { value: 'create', label: 'Create' },
];

export interface ModuleObjectiveInput {
  description: string;
  bloom_level: string;
}

export interface ModuleFormValues {
  title: string;
  description: string;
  estimated_duration: { hours: number; minutes: number };
  learning_objectives: ModuleObjectiveInput[];
}

interface ModuleFormModalProps {
  isOpen: boolean;
  /** 'add' shows creation copy, 'edit' pre-fills from `module`. */
  mode: 'add' | 'edit';
  /** The module being edited (edit mode only). */
  module?: Module | null;
  /** 1-based position the new module will occupy (add mode only) — display hint. */
  insertPosition?: number;
  /** Parent performs the API call + refetch. Throw to keep the modal open. */
  onSave: (data: ModuleFormValues) => Promise<void>;
  onClose: () => void;
}

export default function ModuleFormModal({
  isOpen,
  mode,
  module,
  insertPosition,
  onSave,
  onClose,
}: ModuleFormModalProps) {
  const [isSaving, setIsSaving] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ModuleFormValues>({
    defaultValues: {
      title: '',
      description: '',
      estimated_duration: { hours: 0, minutes: 0 },
      learning_objectives: [],
    },
  });

  const objectives = watch('learning_objectives') || [];

  // Reset form whenever the modal opens or the target module changes.
  useEffect(() => {
    if (!isOpen) return;
    reset({
      title: module?.title || '',
      description: module?.description || '',
      estimated_duration: {
        hours: module?.estimated_duration?.hours || 0,
        minutes: module?.estimated_duration?.minutes || 0,
      },
      learning_objectives:
        module?.learning_objectives?.map((o) => ({
          description: o.description || o.objective || '',
          bloom_level: (o.bloom_level || 'understand').toLowerCase(),
        })) || [],
    });
  }, [isOpen, module, reset]);

  if (!isOpen) return null;

  const addObjective = () => {
    setValue('learning_objectives', [
      ...objectives,
      { description: '', bloom_level: 'understand' },
    ]);
  };

  const updateObjective = (
    index: number,
    field: keyof ModuleObjectiveInput,
    value: string
  ) => {
    const next = [...objectives];
    next[index] = { ...next[index], [field]: value };
    setValue('learning_objectives', next);
  };

  const removeObjective = (index: number) => {
    setValue(
      'learning_objectives',
      objectives.filter((_, i) => i !== index)
    );
  };

  const onSubmit = async (data: ModuleFormValues) => {
    // Drop empty objective rows and sanitize free text before persisting.
    const cleaned: ModuleFormValues = {
      title: cleanText(data.title),
      description: cleanText(data.description || ''),
      estimated_duration: {
        hours: Number(data.estimated_duration?.hours) || 0,
        minutes: Number(data.estimated_duration?.minutes) || 0,
      },
      learning_objectives: (data.learning_objectives || [])
        .map((o) => ({
          description: cleanText(o.description || ''),
          bloom_level: o.bloom_level || 'understand',
        }))
        .filter((o) => o.description.length > 0),
    };

    setIsSaving(true);
    try {
      await onSave(cleaned);
    } catch {
      // Parent surfaces the error toast; keep the modal open so edits aren't lost.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-blue-600 flex items-center justify-center">
              <Layers className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {mode === 'add' ? 'Add Module' : 'Edit Module'}
              </h2>
              <p className="text-xs text-gray-500">
                {mode === 'add'
                  ? insertPosition
                    ? `Will be inserted at position ${insertPosition}`
                    : 'Added to the end of the course'
                  : 'Update module details'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <InputField
            label="Module Title"
            required
            error={errors.title?.message as string}
            placeholder="e.g., Introduction to React Hooks"
            {...register('title', {
              required: 'Module title is required',
              minLength: { value: 3, message: 'Title must be at least 3 characters' },
              maxLength: { value: 200, message: 'Title must not exceed 200 characters' },
              validate: (v: string) =>
                isCleanText(v) || 'Please use only letters, numbers and standard punctuation',
            })}
          />

          <Textarea
            name="description"
            control={control}
            label="Description"
            rows={3}
            placeholder="What this module covers"
            rules={{
              maxLength: { value: 2000, message: 'Description must not exceed 2000 characters' },
              validate: (v: string) =>
                !v || isCleanText(v) || 'Please use only letters, numbers and standard punctuation',
            }}
          />

          {/* Estimated duration */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4 text-amber-500" />
              Estimated Duration
            </label>
            <div className="grid grid-cols-2 gap-3 max-w-xs">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Hours</label>
                <input
                  type="number"
                  min={0}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                  {...register('estimated_duration.hours', { valueAsNumber: true, min: 0 })}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Minutes</label>
                <input
                  type="number"
                  min={0}
                  max={59}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                  {...register('estimated_duration.minutes', { valueAsNumber: true, min: 0, max: 59 })}
                />
              </div>
            </div>
          </div>

          {/* Learning objectives */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <BookOpen className="w-4 h-4 text-blue-500" />
                Learning Objectives
              </label>
              <button
                type="button"
                onClick={addObjective}
                className="px-3 py-1 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 transition-colors text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Objective
              </button>
            </div>

            <div className="space-y-2">
              {objectives.map((objective, index) => (
                <div
                  key={index}
                  className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-start border rounded-lg p-3 bg-gray-50"
                >
                  <input
                    type="text"
                    value={objective.description}
                    onChange={(e) => updateObjective(index, 'description', e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500 text-sm"
                    placeholder="What learners will be able to do"
                  />
                  <select
                    value={objective.bloom_level}
                    onChange={(e) => updateObjective(index, 'bloom_level', e.target.value)}
                    className="w-full sm:w-44 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500 text-sm bg-white"
                  >
                    {BLOOM_LEVELS.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => removeObjective(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors self-center"
                    title="Remove objective"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {objectives.length === 0 && (
                <div className="text-gray-400 text-sm py-4 text-center border-2 border-dashed rounded-lg">
                  No learning objectives yet (optional).
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-5 py-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit(onSubmit, () => toast.error('Please fix the highlighted fields'))}
            disabled={isSaving}
            className="px-5 py-2 text-sm bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 transition-colors disabled:opacity-50 font-medium flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>{mode === 'add' ? 'Add Module' : 'Save Changes'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
